import React, { useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
interface Role {
  title: string;
  company: string;
  period: string;
  bullets: string[];
}
const roles: Role[] = [{
  title: 'Value Stream Leader',
  company: 'Pall – Deland, FL',
  period: '10/2021 – 05/2023',
  bullets: ['Directed the production output of 7 manufacturing cells with 23 skilled operators; reallocated resources daily due to schedule requirements', 'Successfully implemented a new production scheduling system, resulting in a 65% increase in on-time delivery performance and a 40% reduction in lead times', 'Improved product quality by collaborating with engineering, procurement, and quality assurance teams to align production plans with company objectives', 'Increased retention through employee engagement, providing coaching and feedback to foster a culture of accountability and continuous growth']
}, {
  title: 'Project Manager / Production Supervisor',
  company: 'Delta Engineering – Duluth, GA',
  period: '08/2018 – 09/2021',
  bullets: ['Coordinated and managed all aspects of 75+ automation projects ($12.65M), including planning, scheduling, resource allocation, and budget management', 'Led the production team to achieve an 80% increase in productivity by implementing new production scheduling techniques across 34 automated lines', 'Utilized data analytics tools to monitor KPIs, track production metrics, and generate comprehensive reports for senior management']
}, {
  title: 'Maintenance Manager (Training Program)',
  company: 'WCA – Gainesville, FL',
  period: '02/2018 – 06/2018',
  bullets: ['Responsible for the effective management of maintenance, upkeep, servicing, troubleshooting, and repair of company heavy equipment', 'Developed and implemented maintenance strategies, policies, and procedures to optimize equipment reliability and minimize downtime', 'Selected as the first Maintenance Manager Trainee in company history']
}, {
  title: "Veteran's Home Director",
  company: 'Chennault Place – Lake Charles, LA',
  period: '08/2015 – 08/2017',
  bullets: ['Trusted to act as a Fiduciary for 25 severely handicapped veterans, safeguarding a monthly average of $75K', 'Reduced monthly deficit of $40K to a profit of $6K within eight months', 'Renegotiated supply vendor service terms reducing monthly cost by 33%', 'Enhanced resident engagement programs, resulting in increased participation rates and improved quality of life']
}, {
  title: 'Operations Manager',
  company: 'US Navy – Worldwide',
  period: 'Early Experience',
  bullets: ['Directed aircraft maintenance operations for land and sea-based F/A-18 squadrons including operations, maintenance, logistics, and long/short-term planning', 'Promoted to the top 2.5% of all Naval enlisted personnel', 'Ten years of authority to release aircraft safe for flight; zero mishaps across entire tenure', 'Led F/A-18 Lean Manufacturing event, reduced labor costs by 40%']
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    x: -30
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
const headingVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: 'easeOut'
    }
  }
};
export function ExperienceTimeline() {
  const ref = useRef<HTMLElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section id="experience" ref={ref} className="py-20" style={{
    backgroundColor: '#ffffff'
  }} data-id="element-86">
      <div className="max-w-5xl mx-auto px-6" data-id="element-87">
        {/* Heading */}
        <motion.div variants={headingVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="mb-14" data-id="element-88">
          <h2 className="font-display" style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(28px, 4vw, 38px)',
          fontWeight: 700,
          letterSpacing: '0.05em',
          color: '#0d1b4b',
          paddingBottom: '10px',
          borderBottom: '2px solid var(--gold)',
          display: 'inline-block'
        }} data-id="element-89">
            PROFESSIONAL EXPERIENCE
          </h2>
        </motion.div>

        {/* Timeline */}
        <div className="relative" data-id="element-90">
          {/* Vertical gold line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 hidden md:block" style={{
          background: 'linear-gradient(to bottom, var(--gold), rgba(201,150,62,0.1))'
        }} data-id="element-91" />

          <motion.div variants={containerVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} className="space-y-8" data-id="element-92">
            {roles.map((role, index) => <motion.div key={index} variants={cardVariants} className="relative md:pl-14" data-id="element-93">
                {/* Gold dot marker */}
                <div className="absolute left-0 top-6 w-3 h-3 rounded-full border-2 hidden md:block" style={{
              backgroundColor: '#ffffff',
              borderColor: 'var(--gold)',
              boxShadow: '0 0 8px rgba(201,150,62,0.4)'
            }} data-id="element-94" />

                {/* Card */}
                <div className="p-6 rounded-md bg-white" style={{
              border: '1px solid rgba(13,27,75,0.1)',
              borderLeft: '3px solid var(--gold)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }} data-id="element-95">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-5" data-id="element-96">
                    <div data-id="element-97">
                      <h3 className="font-display" style={{
                    fontFamily: 'Playfair Display, serif',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#0d1b4b'
                  }} data-id="element-98">
                        {role.title}
                      </h3>
                      <p className="mt-0.5 font-semibold" style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    color: 'var(--gold)',
                    letterSpacing: '0.02em'
                  }} data-id="element-99">
                        {role.company}
                      </p>
                    </div>
                    <span className="shrink-0 px-3 py-1 rounded-md text-xs font-medium" style={{
                  fontFamily: 'Inter, sans-serif',
                  color: '#1a3a8f',
                  backgroundColor: '#eff6ff',
                  border: '1px solid rgba(26,58,143,0.2)',
                  letterSpacing: '0.05em'
                }} data-id="element-100">
                      {role.period}
                    </span>
                  </div>

                  {/* Bullets */}
                  <ul className="space-y-2.5" data-id="element-101">
                    {role.bullets.map((bullet, bIndex) => <li key={bIndex} className="flex items-start gap-3" style={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#4b5563',
                  lineHeight: '1.7'
                }} data-id="element-102">
                        <span className="shrink-0 mt-1.5 text-xs" style={{
                    color: 'var(--gold)'
                  }} data-id="element-103">
                          ◆
                        </span>
                        <span data-id="element-104">{bullet}</span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>)}
          </motion.div>
        </div>
      </div>
    </section>;
}